import React, { useState } from 'react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { CalendarBlank, CheckCircle } from '@phosphor-icons/react';

/**
 * CalendarConnectCard — one-tap connection of a Google Calendar read-only
 * scope so the Almanac can softly notice birthdays / dates / vigils.
 * The backend endpoint returns 503 until GOOGLE_OAUTH_CLIENT_SECRET is set,
 * so this UI gracefully reports "not yet configured" in that case.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT
 * URLS, THIS BREAKS THE AUTH
 */
const CalendarConnectCard = ({ user }) => {
  const [busy, setBusy] = useState(false);
  const connected = !!user?.google_calendar_connected_at;

  const connect = () => {
    const cid = process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID;
    if (!cid) { toast.error('google oauth not configured'); return; }
    setBusy(true);
    const redirect = `${window.location.origin}/profile`;
    const url = `https://accounts.google.com/o/oauth2/v2/auth?` + new URLSearchParams({
      client_id: cid,
      redirect_uri: redirect,
      response_type: 'code',
      access_type: 'offline',
      prompt: 'consent',
      include_granted_scopes: 'true',
      scope: 'https://www.googleapis.com/auth/calendar.events.readonly',
    });
    window.location.href = url;
  };

  // If we landed back with ?code=...
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    if (!code || connected) return;
    setBusy(true);
    api.post('/auth/google/calendar/exchange', {
      code,
      redirect_uri: `${window.location.origin}/profile`,
    }).then(() => {
      toast.success('your bubble can now glance at your week');
      window.history.replaceState({}, '', '/profile');
    }).catch((err) => {
      const msg = err?.response?.data?.detail || 'could not connect';
      if (msg.toString().includes('not configured')) {
        toast.message('calendar scope not yet activated server-side');
      } else { toast.error(msg); }
    }).finally(() => setBusy(false));
  }, [connected]);

  return (
    <div className="glass rounded-2xl p-5 mt-6" data-testid="calendar-connect-card">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-bx-cyan flex items-center gap-1">
            <CalendarBlank size={11} weight="duotone" /> almanac · calendar scope
          </div>
          <div className="font-display text-lg font-bold mt-1">
            {connected ? 'Your bubble glances at your week' : 'Let your bubble see your week'}
          </div>
          <p className="text-xs text-white/60 mt-1 max-w-md">
            {connected
              ? 'Read-only · titles and dates only · we forget after 7 days. Disconnect anytime.'
              : 'We only read titles and start dates of the next 7 days. Body, attendees, links are never requested.'}
          </p>
        </div>
        {connected ? (
          <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-bx-cyan/15 text-bx-cyan text-[11px] font-bold uppercase tracking-widest">
            <CheckCircle size={11} weight="fill" /> connected
          </span>
        ) : (
          <button onClick={connect} disabled={busy}
            data-testid="calendar-connect-btn"
            className="px-4 py-2 rounded-full bg-bx-cyan/15 hover:bg-bx-cyan/25 text-bx-cyan text-xs font-bold uppercase tracking-widest disabled:opacity-50">
            {busy ? 'opening…' : 'connect'}
          </button>
        )}
      </div>
    </div>
  );
};

export default CalendarConnectCard;
